/* eslint-disable no-fallthrough */
import { FieldValues, Path, mapDropDown } from '@ninja/utilities';
import { Gender, MaritalStatus, Relationship } from '../../../../../common';
import { useAppPlatformContextOptions } from '../../../../../contexts/app';
import { useRelationships, useSystemConfigurationSettings } from '../../../../app-init-data';
import { IEidMembersForm } from '../../../with-eid-members/members-form/types';
import { IMembersForm } from '../../../without-eid-members';
import { AddFieldError } from '../helpers/add-field-error';
import { isRelationshipCountValid } from '../helpers/is-relationship-count-valid';

export const useRelationshipField = <TForm extends FieldValues>() => {
	const { translate: t } = useAppPlatformContextOptions();
	const { relationships } = useRelationships();
	const { systemConfigurationSettings } = useSystemConfigurationSettings();

	const getField = ({ field }: { field: Path<TForm> }) =>
		mapDropDown({
			field,
			label: t('members:fields.relationship.label'),
			placeholder: t('members:fields.relationship.placeholder'),
			options: relationships.filter(
				(r) =>
					![Relationship.Self, Relationship.DomesticWorker, Relationship.Father, Relationship.Mother].includes(r.id)
			),
			valueKey: 'id',
			labelKey: 'name',
		});
	const getValidation = ({
		formValues,
		addFieldError,
		relationshipTypeId,
	}: {
		formValues: IEidMembersForm | IMembersForm;
		addFieldError: ({ field, message }: AddFieldError) => void;
		relationshipTypeId: number;
	}) => {
		switch (relationshipTypeId) {
			case Relationship.Father:
				// User should be able to add one Father
				if (!isRelationshipCountValid(formValues, relationshipTypeId, [Relationship.Father])) {
					addFieldError({
						field: 'relationshipTypeId',
						message: t('members:fields.relationship.validation.max'),
					});
					break;
				}

			case Relationship.Mother: {
				// User should be able to add one Mother
				if (!isRelationshipCountValid(formValues, relationshipTypeId, [Relationship.Mother]))
					addFieldError({
						field: 'relationshipTypeId',
						message: t('members:fields.relationship.validation.max'),
					});
				break;
			}

			case Relationship.Spouse: {
				// User should be able to add spouses to principal is not single
				if (formValues.principalDetails.maritalStatusId === MaritalStatus.Single) {
					addFieldError({
						field: 'relationshipTypeId',
						message: t('members:fields.relationship.validation.spouse.single'),
					});
				}
				// User should be able to add up to 4 spouses
				// maxNoOfSpouse
				else if (
					!isRelationshipCountValid(
						formValues,
						relationshipTypeId,
						[Relationship.Spouse],
						formValues.principalDetails.genderId === Gender.Male ? systemConfigurationSettings?.maximumSpouseCount : 1
					)
				)
					addFieldError({
						field: 'relationshipTypeId',
						message: t('members:fields.relationship.validation.max'),
					});
				break;
			}

			case Relationship.Daughter:
			case Relationship.Son: {
				// User should be able to add up to 10 Son/Daughter, total children up to 10 , including daughter and son
				// maxNoOfChildren
				if (
					!isRelationshipCountValid(
						formValues,
						relationshipTypeId,
						[Relationship.Daughter, Relationship.Son],
						systemConfigurationSettings?.maximumChildrenCount
					)
				)
					addFieldError({
						field: 'relationshipTypeId',
						message: t('members:fields.relationship.validation.max'),
					});
				break;
			}

			default:
				throw new Error('unsupported fieldType');
		}
	};

	return {
		getField,
		getValidation,
	};
};
